import * as mobx from 'mobx';
import * as IoC from 'typescript-ioc';
import { alertServerError } from '../../../../../global/modal/alerts-host/alert-templates';
import { IErrorParsing } from '../../../../../../infrastructure/error-parsing';
import { IConnectionDetailsDTO, IConnectionsDAO } from '../../../../../../api/connections.dao';
import { IntlShape } from 'react-intl';
import { IReactIntlProvider } from '../../../../../../infrastructure/react-intl-provider';
import { CreatePanelistLinkFormDataVM } from './create-panelist-link-form-data.viewmodel';
import { IAttendeeOrganizationDTO } from '../../../../../../api/attendees.dao';
import TrackingServiceProvider from '../../../../../../tracking-service/tracking-service-provider';
import { IGlobalStorage } from '../../../../../../infrastructure/global-storage';
import { TRACKING_EVENTS } from '../../../../../../tracking-service/tracking-values';
import { ConnectionVersion } from '../../../../../../constants/common.constants';

class CreatePanelistLinkVM {
    @mobx.observable
    private isFormShown: boolean;
    @mobx.observable
    private isSaving: boolean;
    @mobx.observable
    public createPanelistLinkFormDataVM: CreatePanelistLinkFormDataVM;

    constructor(
        private readonly webinarDetails: IConnectionDetailsDTO[],
        private readonly presentersList: IAttendeeOrganizationDTO[],
        private readonly hostsList: IAttendeeOrganizationDTO[],
        private readonly supportsList: IAttendeeOrganizationDTO[],
        public readonly createPanelistLinkConnectionCallback: (connectionDetails?: IConnectionDetailsDTO) => void,
        private event: { eventId: number; accountId: number; meetingId?: string | number},
        private readonly connectionVersion?: ConnectionVersion,
        private readonly errorParsingService: IErrorParsing = IoC.Container.get(IErrorParsing),
        @IoC.Inject private readonly intlProvider: IReactIntlProvider = IoC.Container.get(IReactIntlProvider),
        @IoC.Inject private readonly connectionDAO: IConnectionsDAO = IoC.Container.get(IConnectionsDAO),
        @IoC.Inject private readonly trackingService: TrackingServiceProvider = IoC.Container.get(TrackingServiceProvider),
        @IoC.Inject private readonly globalStorage: IGlobalStorage = IoC.Container.get(IGlobalStorage),
    ) {
        this.isFormShown = false;
        this.isSaving = false;
        this.createPanelistLinkFormDataVM = new CreatePanelistLinkFormDataVM(
            this.webinarDetails,
            this.presentersList,
            this.hostsList,
            this.supportsList,
            this.connectionVersion,
        );
    }

    public get intl(): IntlShape { return this.intlProvider.getIntl(); }

    @mobx.action
    public showForm = (): void => {
        this.isFormShown = true;
    };

    @mobx.action
    public hideForm = (): void => {
        this.createPanelistLinkFormDataVM.cleanup();
        this.isFormShown = false;
    };

    public createPanelistLinkHandle = async (): Promise<void> => {
        let connectionDetails: IConnectionDetailsDTO;
        if (this.isSaving) {
            return;
        }

        mobx.runInAction(() => {
            this.isSaving = true;
        });

        try {
            const panelistLink = this.createPanelistLinkFormDataVM.getAPIDTO();
            const response = await this.connectionDAO.scheduleNewPanelistLink(panelistLink);
            connectionDetails = {
                ...panelistLink,
                connectionId: panelistLink.webinarID,
                label: `${response.name}'s Link`,
                attendeeId: panelistLink.attendeeID,
                connectionLink: response.joinUrl,
                connectionAccount: response.connectionAccount,
                attendeeEmail: response.email,
                connectionVersion: this.connectionVersion,
                parentJoinMethodId: panelistLink.parentJoinMethodId,
                parentMeetingConnectionId: panelistLink.parentMeetingConnectionId
            };
        } catch (error) {
            const errorMessage = this.errorParsingService.extractMessage(error);
            alertServerError('Error', errorMessage);
            return;
        } finally {
            mobx.runInAction(() => {
                this.isSaving = false;
            });
        }

        this.trackCreatePanelistLinkMixPanel(TRACKING_EVENTS.CREATE_PANELIST_LINK, connectionDetails.attendeeId);
        this.createPanelistLinkConnectionCallback(connectionDetails);
        this.hideForm();
    };

    public trackCreatePanelistLinkMixPanel = (eventName: string, attendeeId: number): void => {
        this.trackingService.get().track(eventName,{
            'Account ID': this.globalStorage.getAccount().accountId,
            'Account Name': this.globalStorage.getAccount().name,
            'Event ID': this.globalStorage.getEvent().eventId,
            'Event Name': this.globalStorage.getEvent().name,
            'Meeting ID': this.event.meetingId,
            'Attendee ID': attendeeId,
        });
    };

    public openPanelistLinkConnectionForm(connectionDetails: IConnectionDetailsDTO): void {
        this.createPanelistLinkConnectionCallback(connectionDetails);
        this.hideForm();
    };

    public getIsFormShown = (): boolean => {
        return this.isFormShown;
    };

    public getIsFormSaving = (): boolean => {
        return this.isSaving;
    };
}

export { CreatePanelistLinkVM };